/**
 * Light / dark, as a class on `<html>`.
 *
 * Tailwind is configured `darkMode: ['class']`, so `.dark` is the switch the
 * whole design system already listens to — including Phase 5's chart tokens,
 * whose dark steps were selected and validated against the dark surface rather
 * than flipped from the light ones.
 *
 * It exists because Phase 5 has to be **legible in both themes and readable
 * when projected**, and a theme nothing can select is a claim nobody can
 * check. Three states rather than two: `system` is the default and follows the
 * OS, which is what a laptop plugged into a projector in a dim room usually
 * has already decided.
 */

import { useCallback, useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';

export type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'menlo-funnel.theme';

export function readThemeChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    // A private window, or site data blocked. A theme is a convenience; the
    // page must render regardless.
    return 'system';
  }
}

export function applyTheme(choice: ThemeChoice): void {
  const prefersDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = choice === 'dark' || (choice === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', dark);
}

const OPTIONS: { value: ThemeChoice; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

export function ThemeToggle(): React.ReactElement {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());

  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    // Follow the OS while "system" is selected, not only at first paint.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (): void => applyTheme('system');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [choice]);

  const select = useCallback((next: ThemeChoice) => {
    setChoice(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Nothing to do — the choice still applies for this page load.
    }
  }, []);

  return (
    <div className="flex rounded-md border p-0.5" role="group" aria-label="Colour theme">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={choice === value}
          aria-label={label}
          title={label}
          onClick={() => select(value)}
          className={`rounded px-1.5 py-1 ${
            choice === value ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
          }`}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
